/** Role-aware landing page. Tenant users get the viobean-style learner
 * dashboard: greeting, AI-coach banner, lesson mastery, exam skill
 * mastery per section (with board tabs), and curated focus areas.
 * Super-admins get the platform welcome. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as examsApi from "../../api/exams";
import * as learningApi from "../../api/learning";
import { useAuth } from "../../auth/AuthContext";
import { Button, Card, ProgressRing } from "../../components/ui";
import { useT } from "../../i18n";
import { KoennenCheck } from "../learning/KoennenCheck";
import { DiamondBoard } from "./DiamondBoard";
import { RecommendationCard } from "./RecommendationCard";

export function greetingKeyForHour(hour: number): "dashboard.greeting.morning" | "dashboard.greeting.day" | "dashboard.greeting.evening" {
  if (hour < 11) return "dashboard.greeting.morning";
  if (hour < 18) return "dashboard.greeting.day";
  return "dashboard.greeting.evening";
}

export function overallMastery(rows: learningApi.CourseProgress[]): number {
  if (rows.length === 0) return 0;
  return Math.round(rows.reduce((sum, r) => sum + r.mastery_percent, 0) / rows.length);
}

/** Mean best-score across a section's non-empty parts. */
export function sectionMastery(section: examsApi.ExamSection): number {
  const parts = section.parts.filter((p) => p.item_count > 0);
  if (parts.length === 0) return 0;
  return Math.round(parts.reduce((sum, p) => sum + p.avg_best_score, 0) / parts.length);
}

export function examMastery(exam: examsApi.ExamOverview): number {
  const sections = exam.sections.filter((s) => s.parts.some((p) => p.item_count > 0));
  if (sections.length === 0) return 0;
  return Math.round(sections.reduce((sum, s) => sum + sectionMastery(s), 0) / sections.length);
}

/** Curated per-level grammar focus areas (viobean-style hints) —
 * static v1; personal focus areas from practice data come later. */
const FOCUS_AREAS = [
  { title: "Dativ vs. Akkusativ", key: "dashboard.focus.cases" as const },
  { title: "Nebensätze", key: "dashboard.focus.clauses" as const },
  { title: "Modalverben", key: "dashboard.focus.modals" as const },
];

/** Super-admins run the platform, they don't practise — their dashboard is a
 * set of jump-off points to the operator surfaces, not learner mastery. */
const ADMIN_LINKS = [
  { to: "/tenants", icon: "🏫", key: "nav.tenants", desc: "dashboard.admin.tenants" },
  { to: "/orders", icon: "🧾", key: "nav.orders", desc: "dashboard.admin.orders" },
  { to: "/revenue", icon: "📈", key: "nav.revenue", desc: "dashboard.admin.revenue" },
  { to: "/ai-settings", icon: "🤖", key: "nav.ai", desc: "dashboard.admin.ai" },
] as const;

export function DashboardPage({ now = () => new Date() }: { now?: () => Date }) {
  const t = useT();
  const { user } = useAuth();
  const [progress, setProgress] = useState<learningApi.CourseProgress[]>([]);
  const [exams, setExams] = useState<examsApi.ExamOverview[]>([]);
  const [activeExam, setActiveExam] = useState(0);
  const greeting = t(greetingKeyForHour(now().getHours()));
  const isTenantUser = Boolean(user?.tenant_id);

  useEffect(() => {
    if (!isTenantUser) return;
    let cancelled = false;
    learningApi
      .myProgress()
      .then((rows) => {
        if (!cancelled) setProgress(rows);
      })
      .catch(() => {
        // Learning module may be off for this tenant — dashboard stays
        // useful without the mastery card data.
      });
    examsApi
      .listExams()
      .then((rows) => {
        if (!cancelled) setExams(rows);
      })
      .catch(() => {
        // Exam-prep module off → the skill section simply stays hidden.
      });
    return () => {
      cancelled = true;
    };
  }, [isTenantUser]);

  const mastery = overallMastery(progress);
  const exam = exams[Math.min(activeExam, Math.max(exams.length - 1, 0))];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold text-ink">
          {greeting}, {user?.full_name} <span aria-hidden>👋</span>
        </h1>
        <p className="mt-1 text-muted">{t("dashboard.subtitle")}</p>
      </div>

      {/* What a freshly-onboarded self-serve learner should buy — the most
          actionable thing for someone who owns nothing yet. Self-hiding once
          they own the level. */}
      {isTenantUser && <RecommendationCard />}

      {/* Where the learner stands and what to do next — the first thing
          worth seeing on the dashboard. Super-admins have no progress. */}
      {isTenantUser && <KoennenCheck />}

      {/* What they have earned so far. Sits high because for the younger
          half of the audience it is the reason to come back. */}
      {isTenantUser && <DiamondBoard />}

      {isTenantUser && (
        <div className="bg-grad-hero rounded-card p-6 text-white shadow-lg">
          <h2 className="text-lg font-bold">✍️ {t("dashboard.aiTitle")}</h2>
          <p className="mt-1 max-w-2xl text-sm text-white/85">{t("dashboard.aiBody")}</p>
          <Link to="/practice" className="mt-3 inline-block">
            <Button variant="secondary">{t("dashboard.aiCta")} →</Button>
          </Link>
        </div>
      )}

      {isTenantUser && (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Card title="Overall mastery" tone="brand">
          <div className="flex items-center gap-4 sm:gap-6">
            <ProgressRing percent={mastery} label="overall mastery" />
            {progress.length === 0 ? (
              <p className="text-sm text-muted">
                Start practicing to build your overall mastery.
              </p>
            ) : (
              <ul className="min-w-0 flex-1 space-y-2">
                {progress.map((row) => (
                  <li key={row.course_id}>
                    <div className="flex justify-between text-sm">
                      <span className="font-semibold text-ink">
                        {row.language_code.toUpperCase()} {row.level_code}
                      </span>
                      <span className="text-muted">{row.mastery_percent}%</span>
                    </div>
                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-line">
                      <div
                        className="h-full rounded-full bg-brand"
                        style={{ width: `${row.mastery_percent}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
        <Card title="This week" tone="brand">
          {progress.some((row) => row.lessons_completed > 0) ? (
            <p className="text-sm text-muted">
              {progress.reduce((sum, row) => sum + row.lessons_completed, 0)}{" "}
              {t("learn.lessons")} · {progress.reduce((sum, row) => sum + row.xp_earned, 0)}{" "}
              {t("learn.xp")}
            </p>
          ) : (
            <p className="text-sm text-muted">Start your first practice this week!</p>
          )}
          {isTenantUser && (
            <Link to="/learn" className="mt-4 inline-block">
              <Button>{t("nav.learn")} →</Button>
            </Link>
          )}
        </Card>
      </div>
      )}

      {!isTenantUser && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {ADMIN_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="rounded-card border border-line bg-surface p-5 transition-all hover:-translate-y-0.5 hover:shadow-card"
            >
              <p className="text-lg font-bold text-ink">
                <span aria-hidden>{link.icon}</span> {t(link.key)}
              </p>
              <p className="mt-1 text-sm text-muted">{t(link.desc)}</p>
            </Link>
          ))}
        </div>
      )}

      {isTenantUser && exam && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card
            title={t("dashboard.examSkills")}
            tone="brand"
            actions={
              // Ten boards x levels will never fit a phone header. A rail
              // that scrolls sideways INSIDE itself keeps them all
              // reachable without squeezing the page.
              <div className="-mx-1 flex max-w-full gap-1 overflow-x-auto px-1 pb-1">
                {exams.map((option, i) => (
                  <button
                    key={`${option.board}-${option.level}`}
                    onClick={() => setActiveExam(i)}
                    className={`shrink-0 rounded-control border px-2 py-1 text-xs font-semibold ${i === activeExam ? "border-brand bg-brand-soft text-brand-dark" : "border-line text-muted hover:text-ink"}`}
                  >
                    {option.board} {option.level}
                  </button>
                ))}
              </div>
            }
          >
            <div className="flex items-start gap-4 sm:gap-6">
              <ProgressRing
                percent={examMastery(exam)}
                size={72}
                label={`${exam.board} mastery`}
              />
              <ul className="min-w-0 flex-1 space-y-3">
                {exam.sections
                  .filter((s) => s.parts.some((p) => p.item_count > 0))
                  .map((section) => {
                    const value = sectionMastery(section);
                    return (
                      <li key={section.code}>
                        <div className="flex justify-between text-sm">
                          <span className="font-semibold text-ink">
                            <span aria-hidden>{section.icon}</span> {section.name}
                          </span>
                          <span className="text-muted">{value}%</span>
                        </div>
                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-line">
                          <div
                            className="h-full rounded-full bg-brand"
                            style={{ width: `${value}%` }}
                          />
                        </div>
                      </li>
                    );
                  })}
              </ul>
            </div>
            <p className="mt-3 text-xs text-muted">{t("dashboard.examHint")}</p>
          </Card>

          <Card title={t("dashboard.focusAreas")} tone="accent">
            <p className="text-xs uppercase text-muted">{t("dashboard.focusCommon")}</p>
            <ul className="mt-3 space-y-3">
              {FOCUS_AREAS.map((area) => (
                <li key={area.title} className="rounded-control bg-accent-soft/60 p-3">
                  <p className="text-sm font-bold text-ink">💡 {area.title}</p>
                  <p className="text-sm text-muted">{t(area.key)}</p>
                </li>
              ))}
            </ul>
            <Link
              to="/practice"
              className="mt-3 inline-block text-sm font-semibold text-brand hover:underline"
            >
              {t("nav.practice")} →
            </Link>
          </Card>
        </div>
      )}
    </div>
  );
}
